import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Brain, Plus, Trash2, FileText } from 'lucide-react'
import { useDecisionAnalysisStore } from '../stores/decisionAnalysisStore'
import { analyzeMultiOptionsStepByStep } from '../services/api'
import { useAIUsageStore } from '../stores/aiUsageStore'
import { useAuthStore } from '../stores/authStore'
import { DecisionTemplates } from '../components/DecisionTemplates'
import { MultiOptionAnalysis } from '../components/MultiOptionAnalysis'
import { AnalysisProgress } from '../components/AnalysisProgress'

export const MultiOptionAnalysisPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const {
    decisionTitle,
    decisionDescription,
    options,
    criteria,
    isAnalyzing,
    analysisSteps,
    currentAnalysisStep,
    multiOptionResult,
    setDecisionTitle,
    setDecisionDescription,
    addOption,
    removeOption,
    updateOption,
    addCriteria,
    removeCriteria,
    updateCriteria,
    setIsAnalyzing,
    setMultiOptionResult,
    resetDecision,
    addToHistory,
    setOptions,
    setCriteria,
    initializeMultiOptionAnalysisSteps,
    updateAnalysisStep,
    setCurrentAnalysisStep
  } = useDecisionAnalysisStore()

  const { user } = useAuthStore()
  const { canUseAI, incrementUsage, getRemainingUsage } = useAIUsageStore()
  const [showTemplates, setShowTemplates] = useState(false)

  const startAnalysis = async () => {
    if (!user && !canUseAI()) {
      alert(`You have ${getRemainingUsage()} AI calls remaining. Please sign in for unlimited access.`)
      return
    }

    setIsAnalyzing(true)
    initializeMultiOptionAnalysisSteps()
    
    try {
      if (!user) {
        incrementUsage()
      }

      console.log('=== Starting Multi-Option Analysis ===')
      console.log('Request data:', {
        decisionTitle,
        decisionDescription,
        options,
        criteria
      })

      const result = await analyzeMultiOptionsStepByStep(
        {
          decisionTitle,
          decisionDescription,
          options,
          comparisonCriteria: criteria
        },
        (stepId: string, status: 'active' | 'completed' | 'error') => {
          console.log(`Step ${stepId} status updated to:`, status)
          updateAnalysisStep(stepId, status)
          if (status === 'active') {
            setCurrentAnalysisStep(stepId)
          }
        },
        (stepId: string, result: any) => {
          console.log(`Step ${stepId} completed:`, result)
        }
      )

      console.log('=== API Response Received ===')
      console.log('Raw API result:', result)
      console.log('Result type:', typeof result)
      console.log('Result keys:', result ? Object.keys(result) : 'null')

      // Handle different response formats
      let analysisData = null
      if (result && typeof result === 'object') {
        // If result has an analysis property, use that
        if ('analysis' in result && result.analysis) {
          console.log('Found analysis property in result')
          analysisData = result.analysis
        } else if ('success' in result && 'data' in result && result.success && result.data) {
          // If result has success and data properties
          console.log('Found success and data properties in result')
          analysisData = result.data
        } else {
          // Otherwise, use the result directly
          console.log('Using result directly')
          analysisData = result
        }
      }

      console.log('=== Final Analysis Data ===')
      console.log('Analysis data to be set:', analysisData)
      console.log('Analysis data type:', typeof analysisData)
      console.log('Analysis data structure:', analysisData ? JSON.stringify(analysisData, null, 2) : 'null')

      setMultiOptionResult(analysisData)
      addToHistory({
        title: decisionTitle,
        description: decisionDescription,
        options,
        criteria,
        result: analysisData
      })
    } catch (error) {
      console.error('Analysis failed:', error)
      alert('Analysis failed. Please try again.')
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleTemplateSelect = (template: any) => {
    setDecisionTitle(template.title)
    setDecisionDescription(template.fullDescription)

    const templateOptions = template.options.map((opt: string) => ({
      id: Date.now().toString() + Math.random(),
      text: opt,
      pros: [],
      cons: [],
      score: 0
    }))
    const templateCriteria = template.criteria.map((c: any) => ({
      id: Date.now().toString() + Math.random(),
      name: c.name,
      weight: c.weight,
      description: c.description
    }))
    setOptions(templateOptions)
    setCriteria(templateCriteria)
    setShowTemplates(false)
  }

  const renderAnalysisForm = () => (
    <div className="space-y-8">
      {/* Decision Definition */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">What decision are you trying to make?</h3>
          <input
            type="text"
            value={decisionTitle}
            onChange={(e) => setDecisionTitle(e.target.value)}
            placeholder="e.g., Which car should I buy?"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Provide more context</h3>
          <textarea
            value={decisionDescription}
            onChange={(e) => setDecisionDescription(e.target.value)}
            placeholder="Describe your situation, goals, and any relevant factors..."
            rows={4}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Options */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">What are your options?</h3>
          <button
            onClick={addOption}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Option</span>
          </button>
        </div>
        <div className="space-y-4">
          {options.map((option, index) => (
            <div key={option.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-500">Option {index + 1}</span>
                <button
                  onClick={() => removeOption(option.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                value={option.text}
                onChange={(e) => updateOption(option.id, 'text', e.target.value)}
                placeholder="Describe this option..."
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          ))}
          {options.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Brain className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Add your decision options to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Comparison Criteria */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">What criteria will you use to compare options?</h3>
          <button
            onClick={addCriteria}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Criteria</span>
          </button>
        </div>
        <div className="space-y-4">
          {criteria.map((criterion, index) => (
            <div key={criterion.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-500">Criteria {index + 1}</span>
                <button
                  onClick={() => removeCriteria(criterion.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                <input
                  type="text"
                  value={criterion.name}
                  onChange={(e) => updateCriteria(criterion.id, 'name', e.target.value)}
                  placeholder="e.g., Price, Quality, Features, Brand reputation"
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <textarea
                  value={criterion.description}
                  onChange={(e) => updateCriteria(criterion.id, 'description', e.target.value)}
                  placeholder="Why is this important for your decision?"
                  rows={2}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight (1-10): {criterion.weight}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={criterion.weight}
                    onChange={(e) => updateCriteria(criterion.id, 'weight', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          ))}
          {criteria.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Brain className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Add comparison criteria to evaluate your options</p>
            </div>
          )}
        </div>
      </div>

      {/* Analysis Button */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-orange-900 mb-2">Ready for Multi-Option Analysis</h3>
        <p className="text-orange-800 mb-4">
          We'll compare all your options based on the criteria you've defined and provide a detailed comparison matrix.
        </p>
        
        <div className="space-y-4">
          <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <h4 className="font-medium text-gray-900 mb-2">Decision Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 mb-1"><strong>Title:</strong></p>
                <p className="text-gray-900">{decisionTitle || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1"><strong>Options:</strong></p>
                <p className="text-gray-900">{options.length} option{options.length !== 1 ? 's' : ''}</p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-600 mb-1"><strong>Description:</strong></p>
                <p className="text-gray-900">{decisionDescription || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1"><strong>Comparison Criteria:</strong></p>
                <p className="text-gray-900">{criteria.length} criterion{criteria.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={startAnalysis}
            disabled={isAnalyzing || options.length === 0 || criteria.length === 0}
            className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAnalyzing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Multi-Option Analysis in Progress...</span>
              </>
            ) : (
              <>
                <Brain className="w-5 h-5" />
                <span>Start Multi-Option Analysis</span>
              </>
            )}
          </button>
          
          {!isAnalyzing && (options.length === 0 || criteria.length === 0) && (
            <div className="text-sm text-gray-500 text-center">
              {options.length === 0 && <p>• Add at least one option</p>}
              {criteria.length === 0 && <p>• Add at least one comparison criterion</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const handleShowTemplates = () => {
    resetDecision()
    setShowTemplates(true)
  }

  const handleNewAnalysis = () => {
    resetDecision()
    setShowTemplates(false)
  }

  return (
    <>
      <Helmet>
        <title>Multi-option Analysis - AI Decision Maker</title>
        <meta name="description" content="Help make the best choice among multiple options, based on specific criteria comparison" />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Multi-option Analysis</h1>
              <p className="text-gray-600">
                Help make the best choice among multiple options, based on specific criteria comparison
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleNewAnalysis}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" /><span>New</span>
              </button>
              <button
                onClick={handleShowTemplates}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FileText className="w-4 h-4" /><span>Templates</span>
              </button>
            </div>
          </div>
        </div>

        {showTemplates ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <DecisionTemplates onSelectTemplate={handleTemplateSelect} />
          </div>
        ) : (multiOptionResult || isAnalyzing) ? (
          isAnalyzing ? (
            <AnalysisProgress 
              steps={analysisSteps}
              currentStep={currentAnalysisStep}
              analysisType="multi-option"
            />
          ) : (
            <MultiOptionAnalysis 
              result={multiOptionResult}

            />
          )
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {renderAnalysisForm()}
          </div>
        )}
      </div>
    </>
  )
} 